import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import ReactCountryFlag from 'react-country-flag';
import { FaChartLine, FaComments, FaCreditCard, FaFileInvoice, FaGlobeAsia, FaLock, FaMobileAlt, FaRobot, FaShieldAlt, FaTimes } from 'react-icons/fa';
import { BsArrowRight } from 'react-icons/bs';
import { MdVerified } from 'react-icons/md';
import {
  CTASection,
  HeroSection,
  StatsBanner,
  TechnologySection,
  TrustSectionCustomers,
  TrustSectionPartners,
} from './ModernHomePage';
import { googleRating } from '../../utils/business-info';
import { formatMoney } from '../../utils/format-money';
import { MARKETS, getRememberedMarket, rememberMarket, t } from '../../utils/markets';
import { CookieNotice } from '../commons/CookieNotice';

// The global page at "/". StringsSG serves Singapore, the UAE and Malaysia;
// each country has its own home (/sg, /ae, /my) and this page sends visitors
// to the right one. It uses the Singapore homepage's sections and styling, but
// none of its copy - /sg must stay the page that ranks for Singapore searches.
//
// Never redirects. Search engines crawl mostly from the US and must see this
// page; visitors get a highlighted card and a banner suggesting their country
// instead (last country browsed, else the Vercel IP-country header).

const hero = {
  imageAlt: 'StringsSG technicians and personal trainers serving homes in Singapore, the UAE and Malaysia',
  title: 'Trusted Home Services',
  highlight: 'in Singapore, UAE & Malaysia',
  sub: (
    <>
      Aircon servicing and certified personal trainers in Singapore, Dubai, Abu Dhabi and Kuala Lumpur,
      plus handyman, electrician and part-time helper services in Singapore.
      <span className="font-semibold"> Choose your country </span>
      to see local prices and book.
    </>
  ),
  ctas: [
    { href: '#countries', label: 'Choose Your Country' },
    { href: '/about-stringssg', label: 'About StringsSG' },
  ],
  trustLine: 'StringsSG Pte Ltd · UEN 201813375G',
  badges: ['3 Countries', 'Upfront Prices', 'Same Standard Everywhere'],
};

const stats = [
  { value: '2018', label: 'Founded' },
  { value: '3', label: 'Countries' },
  { value: '100K+', label: 'Services Completed' },
  { value: '20K+', label: 'Happy Clients' },
];

const trust = {
  title: 'One Company, Every Country',
  intro: 'Wherever you book, the job is run by StringsSG itself: the same vetting, the same published prices and the same support team behind every technician and trainer.',
  items: [
    { title: 'Checked Before the First Job', body: 'Technicians and trainers are identity- and background-checked in every country before they take a booking' },
    { title: 'Prices in Your Currency', body: 'Dollars in Singapore, dirhams in the UAE, ringgit in Malaysia - always shown before you book' },
    { title: 'Local Know-How', body: 'Service rhythms and training plans built around each city’s climate, homes and routines' },
    { title: 'Support That Answers', body: 'Chat with our team in the app, before and after every job' },
  ],
  card: {
    title: 'Trusted Since 2018',
    body: 'Started in Singapore and now serving Dubai, Abu Dhabi and Kuala Lumpur.',
    stats: [
      { value: googleRating, label: 'Google Rating' },
      { value: '100K+', label: 'Jobs Done' },
    ],
    googleReviews: true,
  },
};

const technology = {
  intro: 'The same StringsSG platform in every country we serve - pricing, booking, chat and records in one place.',
  features: [
    { icon: FaRobot, title: 'RedDotAI Assistant', description: 'Our in-house AI model answers questions and helps you pick the right service, in any of our countries.' },
    { icon: FaChartLine, title: 'Live, Local Pricing', description: 'Prices are worked out from your units, package and area, in the local currency, before you commit.' },
    { icon: FaGlobeAsia, title: 'Three Countries, One Standard', description: 'The same service steps, checks and support in Singapore, the UAE and Malaysia.' },
    { icon: FaLock, title: 'Protected Payments', description: 'Where you pay online, payments are encrypted with TLS 1.3 and handled by a certified payment provider.' },
    { icon: FaShieldAlt, title: 'Accountable Professionals', description: 'Every job is scored, so standards are measured the same way in each city.' },
    { icon: FaComments, title: 'Updates and Chat', description: 'Booking updates, service reminders and a direct line to our team, all in the app.' },
  ],
  appPoints: [
    { icon: FaMobileAlt, text: 'Request and manage your services from your phone' },
    { icon: FaCreditCard, text: 'Prices and payments in your local currency' },
    { icon: FaComments, text: 'Chat with our local team' },
    { icon: FaFileInvoice, text: 'Your receipts and service records, kept together' },
  ],
};

const partners = {
  title: 'Work With StringsSG',
  intro: 'Aircon technicians and personal trainers in Singapore, the UAE and Malaysia: we find the customers and run the admin, so your time goes into the work.',
  items: [
    { title: 'Customers Without Ad Spend', body: 'Bookings come to you through StringsSG' },
    { title: 'Admin Handled', body: 'Scheduling, reminders and invoicing run on our platform' },
    { title: 'Reliable Payouts', body: 'Get paid for every completed job' },
    { title: 'A Profile That Grows', body: 'Ratings and reviews build your reputation with each job' },
  ],
};

const cta = {
  title: 'Looked After, Wherever You Live',
  body: 'Create your StringsSG account and book in Singapore, the UAE or Malaysia.',
};

// Card content per country. Singapore's prices match the /sg page copy; the
// UAE and Malaysian ones come from the pricing profiles (getStaticProps).
const buildCountries = (prices) => {
  const priceFor = (cd) => {
    const market = MARKETS[cd];
    const p = prices?.[cd];
    return [
      { label: t(market, 'acCategory'), price: p ? `${t(market, 'acGeneral')} from ${formatMoney(p.aircon, cd)}` : null },
      { label: 'Personal Trainers', price: p ? `From ${formatMoney(p.pt, cd, 0)} a session` : null },
    ];
  };
  return [
    {
      countryCd: 'sg',
      name: 'Singapore',
      places: 'Island-wide',
      tag: 'Since 2018',
      services: [
        { label: 'Aircon Servicing', price: 'From $16 per unit' },
        { label: 'Personal Trainers', price: 'From $84 a session' },
        { label: 'Handyman & Electricians', price: 'Fixed, published prices' },
        { label: 'Part-Time Helpers', price: 'Flexible scheduling' },
      ],
    },
    {
      countryCd: 'ae',
      name: 'United Arab Emirates',
      places: MARKETS.ae.cities.map((c) => c.name).join(' · '),
      tag: 'New',
      services: priceFor('ae'),
    },
    {
      countryCd: 'my',
      name: 'Malaysia',
      places: MARKETS.my.cities.map((c) => c.name).join(' · '),
      tag: 'New',
      services: priceFor('my'),
    },
  ];
};

const CountryCards = ({ countries, highlighted }) => (
  <section id="countries" className="py-20 md:py-32 bg-slate-50 scroll-mt-14">
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-16">
        <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
          <FaGlobeAsia />
          CHOOSE YOUR COUNTRY
        </span>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4">
          StringsSG Near You
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Each country has its own services, prices and team. Pick yours to see what we offer there.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {countries.map((country) => {
          const market = MARKETS[country.countryCd];
          const isHighlighted = highlighted === country.countryCd;
          return (
            <Link
              key={country.countryCd}
              href={market.homePath}
              onClick={() => rememberMarket(country.countryCd)}
              className={`group flex flex-col bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${isHighlighted ? 'ring-2 ring-orange-500' : ''}`}
            >
              <div className="relative bg-linear-to-br from-orange-500 via-orange-600 to-amber-600 px-6 py-8 text-white">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                </div>
                <div className="relative flex items-center gap-4">
                  <ReactCountryFlag
                    countryCode={country.countryCd.toUpperCase()}
                    svg
                    style={{ width: '3em', height: '2.25em' }}
                    className="rounded-md shadow-lg"
                    aria-hidden="true"
                  />
                  <div>
                    <h3 className="text-2xl font-black leading-tight">{country.name}</h3>
                    <p className="text-white/85 text-sm">{country.places}</p>
                  </div>
                </div>
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm text-xs font-bold px-3 py-1 rounded-full">
                  {isHighlighted ? 'Your country' : country.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <ul className="space-y-3 flex-1">
                  {country.services.map((service) => (
                    <li key={service.label} className="flex items-start gap-3">
                      <MdVerified className="mt-1 shrink-0 text-orange-500" aria-hidden="true" />
                      <div>
                        <p className="font-bold text-slate-900">{service.label}</p>
                        {service.price && <p className="text-sm text-slate-600">{service.price}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
                <span className="mt-6 inline-flex items-center gap-1.5 text-orange-600 font-semibold group-hover:gap-2.5 transition-all">
                  Go to StringsSG {market.shortName}
                  <BsArrowRight />
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Direct links to the busiest service pages. Since "/" became the
          country chooser, the hubs were only reachable from it through the
          nav and footer; the cards above can't hold links of their own
          (each card is one link). */}
      <nav aria-label="Popular in Singapore" className="mt-10 text-center text-sm text-slate-600">
        <span className="font-semibold text-slate-900">Popular in Singapore: </span>
        {POPULAR_SG.map((item, i) => (
          <span key={item.href}>
            {i > 0 && <span aria-hidden="true"> · </span>}
            <Link href={item.href} prefetch={false} onClick={() => rememberMarket('sg')} className="text-orange-600 hover:underline">
              {item.label}
            </Link>
          </span>
        ))}
      </nav>
    </div>
  </section>
);

const POPULAR_SG = [
  { label: 'Aircon servicing', href: '/sg/aircon-servicing-singapore' },
  { label: 'Personal trainers', href: '/sg/personal-trainer' },
  { label: 'Part-time cleaners', href: '/sg/part-time-cleaner-singapore' },
  { label: 'Handyman services', href: '/sg/handyman-singapore-services' },
];

// Suggestion bar, shown in the fixed bottom stack under the cookie notice.
// Client-only (it depends on storage and the visitor's IP), so it renders
// nothing on the server and hydration is unaffected.
const DISMISS_KEY = 'countryBannerDismissed';

const CountryBanner = ({ countryCd, remembered, onDismiss }) => {
  const market = MARKETS[countryCd];
  return (
    <div className="pointer-events-auto" role="region" aria-label="Suggested country">
      <div className="flex items-center gap-3 rounded-2xl bg-slate-900 px-4 py-3 text-white shadow-2xl">
        <ReactCountryFlag countryCode={countryCd.toUpperCase()} svg style={{ width: '1.8em', height: '1.8em' }} aria-hidden="true" />
        <p className="flex-1 text-sm">
          {remembered ? 'Welcome back. ' : `Looks like you're in ${countryCd === 'ae' ? 'the UAE' : market.shortName}. `}
          <Link href={market.homePath} className="font-bold text-amber-300 underline underline-offset-2 hover:text-amber-200">
            Continue to StringsSG {market.shortName}
          </Link>
        </p>
        <button type="button" onClick={onDismiss} className="rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white" aria-label="Dismiss">
          <FaTimes aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const useSuggestedCountry = () => {
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let dismissed = false;
    try {
      dismissed = globalThis.sessionStorage?.getItem(DISMISS_KEY) === '1';
    } catch {}

    // The last country browsed wins; otherwise ask where the visitor is.
    const remembered = getRememberedMarket();
    const lookup = remembered
      ? Promise.resolve({ countryCd: remembered, remembered: true })
      : fetch('/api/geo')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => (data?.countryCd ? { countryCd: data.countryCd, remembered: false } : null));
    lookup
      .then((found) => {
        if (!cancelled && found && MARKETS[found.countryCd]) setSuggestion({ ...found, dismissed });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const dismiss = () => {
    try {
      globalThis.sessionStorage?.setItem(DISMISS_KEY, '1');
    } catch {}
    setSuggestion((s) => (s ? { ...s, dismissed: true } : s));
  };

  return [suggestion, dismiss];
};

export const GlobalHomePage = ({ prices }) => {
  const countries = buildCountries(prices);
  const [suggestion, dismiss] = useSuggestedCountry();

  return (
    <div className="min-h-screen -mt-14">
      <HeroSection hero={hero} />
      <StatsBanner stats={stats} />
      <CountryCards countries={countries} highlighted={suggestion?.countryCd} />
      <TrustSectionCustomers trust={trust} />
      <TechnologySection technology={technology} />
      <TrustSectionPartners partners={partners} />
      <CTASection cta={cta} />
      {/* One fixed stack so the two cards never overlap; the wrapper lets
          clicks through, only the cards themselves take them. */}
      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-30 mx-auto flex max-w-lg flex-col gap-2">
        <CookieNotice />
        {suggestion && !suggestion.dismissed && (
          <CountryBanner countryCd={suggestion.countryCd} remembered={suggestion.remembered} onDismiss={dismiss} />
        )}
      </div>
    </div>
  );
};

export default GlobalHomePage;
